import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallPWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  isStandalone: boolean;
  canPrompt: boolean;
  installPrompt: (() => Promise<void>) | null;
  dismissInstall: () => void;
}

const STORAGE_KEY_DISMISSALS = 'pwa-install-dismissals';
const STORAGE_KEY_VISITS = 'pwa-visit-count';
const MAX_DISMISSALS = 3;
const MIN_VISITS_BEFORE_PROMPT = 3;

export const useInstallPWA = (): InstallPWAState => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    checkIfInstalled();
    checkStandaloneMode();
    incrementVisitCount();

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      localStorage.removeItem(STORAGE_KEY_DISMISSALS);
      localStorage.removeItem(STORAGE_KEY_VISITS);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const checkIfInstalled = async () => {
    if ('getInstalledRelatedApps' in navigator) {
      try {
        const relatedApps = await (navigator as any).getInstalledRelatedApps();
        setIsInstalled(relatedApps.length > 0);
      } catch (error) {
        console.error('Error checking installed apps:', error);
      }
    }
  };

  const checkStandaloneMode = () => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      (window.navigator as any).standalone ||
                      document.referrer.includes('android-app://');
    setIsStandalone(standalone);
    if (standalone) {
      setIsInstalled(true);
    }
  };

  const incrementVisitCount = () => {
    const visits = parseInt(localStorage.getItem(STORAGE_KEY_VISITS) || '0', 10);
    localStorage.setItem(STORAGE_KEY_VISITS, String(visits + 1));
  };

  const getDismissalCount = (): number => {
    return parseInt(localStorage.getItem(STORAGE_KEY_DISMISSALS) || '0', 10);
  };

  const getVisitCount = (): number => {
    return parseInt(localStorage.getItem(STORAGE_KEY_VISITS) || '0', 10);
  };

  const canPrompt = (): boolean => {
    if (!deferredPrompt || isInstalled || isStandalone) {
      return false;
    }

    const dismissals = getDismissalCount();
    const visits = getVisitCount();

    return dismissals < MAX_DISMISSALS && visits >= MIN_VISITS_BEFORE_PROMPT;
  };

  const installPrompt = async (): Promise<void> => {
    if (!deferredPrompt) {
      return;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'dismissed') {
        const dismissals = getDismissalCount();
        localStorage.setItem(STORAGE_KEY_DISMISSALS, String(dismissals + 1));
      }

      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error showing install prompt:', error);
    }
  };

  const dismissInstall = () => {
    const dismissals = getDismissalCount();
    localStorage.setItem(STORAGE_KEY_DISMISSALS, String(dismissals + 1));
    setDeferredPrompt(null);
  };

  return {
    isInstallable: !!deferredPrompt,
    isInstalled,
    isStandalone,
    canPrompt: canPrompt(),
    installPrompt: deferredPrompt ? installPrompt : null,
    dismissInstall,
  };
};
